import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CustomerAdminInfoModel, CustomerService, DebtorModel, DebtorService } from '@railmybox/api-user';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { FormErrorModule, SelectModule } from '@eg/ui';
import { translate, TranslocoModule } from '@jsverse/transloco';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

export type CustomerAndDebtorSelectValue =
  | {
      type: 'customer';
      customer: CustomerAdminInfoModel;
    }
  | {
      type: 'debtor';
      debtor: DebtorModel;
    };

type DropdownItem =
  | {
      type: 'header';
      label: string;
    }
  | {
      type: 'customer';
      customer: CustomerAdminInfoModel;
    }
  | {
      type: 'debtor';
      debtor: DebtorModel;
    };

@UntilDestroy()
@Component({
  selector: 'rmb-customer-and-debtor-select',
  imports: [CommonModule, SelectModule, TranslocoModule, FlexLayoutModule, FormErrorModule],
  templateUrl: './customer-debtor-select.component.html',
  styleUrls: ['./customer-debtor-select.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(CustomerAndDebtorSelectComponent)],
  standalone: true,
})
export class CustomerAndDebtorSelectComponent extends AbstractValueAccessorComponent<CustomerAndDebtorSelectValue> implements OnInit {
  private readonly customerService = inject(CustomerService);
  private readonly debtorService = inject(DebtorService);

  dropdownItems$: Observable<DropdownItem[]>;
  searchControl: FormControl = new FormControl<string | null>(undefined);
  selectedName: string;

  @Input() clearable = true;
  @Input() filterCustomers: string;
  @Input() companyOwner: boolean = undefined;
  @Input() customerList: CustomerAdminInfoModel[] = [];

  get displayErrorMessage(): string {
    return Array.isArray(this.errorMessage) ? this.errorMessage.join(', ') : this.errorMessage;
  }

  ngOnInit(): void {
    const selectedCustomers = this.customerList.map((x) => x.id);

    this.dropdownItems$ = this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((query: string) => {
        if (!query) {
          return of({ customers: [] as CustomerAdminInfoModel[], debtors: [] as DebtorModel[] });
        }

        return forkJoin({
          customers: this.customerService.listCustomers({ isCompanyOwner: this.companyOwner, query }).pipe(catchError(() => of([]))),
          debtors: this.debtorService.adminListDebtors({ query }).pipe(catchError(() => of([]))),
        });
      }),
      map(({ customers, debtors }) => {
        const filteredCustomers = customers
          .filter((item: CustomerAdminInfoModel) => item.active === true)
          .filter((search: CustomerAdminInfoModel) => !selectedCustomers.includes(search.id))
          .filter((customer: CustomerAdminInfoModel) =>
            this.filterCustomers
              ? `${customer.id} ${customer.companyName}`.toLowerCase().includes(this.filterCustomers.toLowerCase())
              : true
          );

        const items: DropdownItem[] = [];

        if (filteredCustomers.length) {
          items.push({ type: 'header', label: translate('customerAndDebtorSelect.customers') });
          items.push(...filteredCustomers.map((customer: CustomerAdminInfoModel) => ({ type: 'customer' as const, customer })));
        }

        if (debtors.length) {
          items.push({ type: 'header', label: translate('customerAndDebtorSelect.debtors') });
          items.push(...debtors.map((debtor: DebtorModel) => ({ type: 'debtor' as const, debtor })));
        }

        return items;
      })
    );
  }

  onSearchValueChanged(query: string): void {
    this.searchControl.patchValue(query);
  }

  onSelect(item?: DropdownItem): void {
    if (!item || item.type === 'header') {
      this.value = undefined;
      this.selectedName = undefined;
      this.onChange({ type: undefined, customer: undefined, debtor: undefined });
      this.changed.emit(undefined);
      return;
    }

    if (item.type === 'customer') {
      this.selectedName = item.customer.companyName;
      this.value = { type: 'customer', customer: item.customer };
    }

    if (item.type === 'debtor') {
      this.selectedName = `${item.debtor.debtorId} - ${item.debtor.name1}`;
      this.value = { type: 'debtor', debtor: item.debtor };
    }

    this.onChange(this.value);
    this.changed.emit(this.value);
  }

  writeValue(value: CustomerAndDebtorSelectValue): void {
    super.writeValue(value);

    if (!value) {
      this.value = undefined;
      this.selectedName = undefined;
      return;
    }

    this.value = value;

    if (value.type === 'customer') {
      this.selectedName = value.customer?.companyName;
      return;
    }

    this.selectedName = value.debtor ? `${value.debtor.debtorId} - ${value.debtor.name1}` : undefined;
  }
}
